import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../theme/ThemeContext';
import { useBreakpoint } from '../hooks/useWindowWidth';
import { getDestinationHierarchy } from '../utils/getDestinationHierarchy';
import { resolveIdentity } from '../seo/identityEngine.js';
import HomeNav from '../components/nav/HomeNav';
import GlobalHeatMap from '../components/maps/GlobalHeatMap';
import DestinationsList from '../components/sections/DestinationsList';

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

export default function AllDestinationsPage({ goCountry, goRegion, goVenue, footerNav }) {
  const C = useTheme();
  const { isMobile, isTablet } = useBreakpoint();
  const isNarrow = isMobile || isTablet;
  const [destinations, setDestinations] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  // The focused country is a STICKY selection shared by the list and the map, not a
  // transient hover: reaching the map means leaving the row, so clearing on mouseleave
  // made the pins vanish mid-journey. One state, so the two can never disagree.
  const [focusedCountry, setFocusedCountry] = useState(null);
  // A region narrows the same focus rather than competing with it, so it always lives
  // under a country and is cleared whenever the country changes — moving back up from
  // a region pill to its country row zooms out instead of leaving a stale region.
  const [focusedRegion, setFocusedRegion] = useState(null);

  function focusCountry(slug) {
    setFocusedCountry(slug);
    setFocusedRegion(null);
  }
  function focusRegion(countrySlug, regionSlug) {
    setFocusedCountry(countrySlug);
    setFocusedRegion(regionSlug);
  }

  useEffect(() => {
    (async () => {
      try {
        const { destinations: d, listings: l } = await getDestinationHierarchy();
        setDestinations(d);
        setListings(l);
      } catch (error) {
        console.error('Error loading destinations:', error);
        setDestinations([]);
        setListings([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const SITE_URL = 'https://luxuryweddingdirectory.com';
  // Title + canonical are owned by the Search Identity Engine (single source of truth) —
  // the page no longer hand-rolls them. Description/og-image stay page-authored.
  const identity = resolveIdentity('/all-destinations', { siteUrl: SITE_URL });

  return (
    <div style={{ background: '#141414', minHeight: '100vh' }}>
      <Helmet>
        <title>{identity.title}</title>
        <meta name="description" content="Explore all luxury wedding destinations where LWD has curated venues and vendors. From Italy to Greece, discover where to celebrate your exceptional wedding." />
        <link rel="canonical" href={identity.canonical} />
        <meta property="og:title" content={identity.title} />
        <meta property="og:description" content="Explore all luxury wedding destinations where LWD has curated venues and vendors." />
        <meta property="og:url" content={identity.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SITE_URL}/og-default.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={identity.title} />
        <meta name="twitter:description" content="Explore all luxury wedding destinations." />
        <meta name="twitter:image" content={`${SITE_URL}/og-default.jpg`} />
      </Helmet>

      <HomeNav darkMode={C.darkMode} onToggleDark={C.toggleDark} />

      <main>
        {/* Hero */}
        <section style={{
          background: '#141414',
          padding: isNarrow ? '120px 24px 40px' : '200px 40px 60px',
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 28, height: 1, background: '#d39a21' }} />
              <span style={{ fontFamily: NU, fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#d39a21', fontWeight: 600 }}>
                EXPLORE
              </span>
              <div style={{ width: 28, height: 1, background: '#d39a21' }} />
            </div>
            <h1 style={{
              fontFamily: GD,
              fontSize: 'clamp(32px, 5vw, 56px)',
              color: '#ffffff',
              lineHeight: 1.1,
              margin: '0 0 16px',
              fontWeight: 400,
            }}>
              All{' '}
              <span style={{ fontStyle: 'italic', color: '#d39a21' }}>Destinations</span>
            </h1>
            <p style={{ fontFamily: NU, fontSize: 16, color: '#b0a89e', lineHeight: 1.6, margin: 0 }}>
              Discover every luxury wedding destination in our directory. From storied European capitals to hidden Mediterranean gems, explore where couples celebrate exceptional weddings.
            </p>
          </div>
        </section>

        {/* Body */}
        {!loading && (
          isNarrow ? (
            /* Mobile/tablet: map on top (fixed height), list below */
            <div>
              <div style={{ height: 280, width: '100%' }}>
                <GlobalHeatMap
                  destinations={destinations}
                  listings={listings}
                  focusedCountry={focusedCountry}
                  focusedRegion={focusedRegion}
                  onCountryFocus={focusCountry}
                  onListingClick={goVenue}
                />
              </div>
              <div style={{ background: C.darkMode ? '#141414' : '#faf8f4' }}>
                {destinations.length === 0 ? (
                  <section style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <p style={{ fontFamily: NU, fontSize: 16, color: C.grey }}>
                      No destinations with listings yet.
                    </p>
                  </section>
                ) : (
                  <DestinationsList
                    destinations={destinations}
                    onCountryClick={goCountry}
                    onRegionClick={goRegion}
                    onCountryFocus={focusCountry}
                    onRegionFocus={focusRegion}
                    activeCountry={focusedCountry}
                    activeRegion={focusedRegion}
                  />
                )}
              </div>
            </div>
          ) : (
            /* Desktop: side-by-side */
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 750px',
              gap: 0,
              alignItems: 'start',
            }}>
              {/* Left: destinations list */}
              <div style={{ paddingLeft: 40, background: C.darkMode ? '#141414' : '#faf8f4', minHeight: '100vh' }}>
                {destinations.length === 0 ? (
                  <section style={{ padding: '80px 0', textAlign: 'center' }}>
                    <p style={{ fontFamily: NU, fontSize: 16, color: C.grey }}>
                      No destinations with listings yet.
                    </p>
                  </section>
                ) : (
                  <DestinationsList
                    destinations={destinations}
                    onCountryClick={goCountry}
                    onRegionClick={goRegion}
                    onCountryFocus={focusCountry}
                    onRegionFocus={focusRegion}
                    activeCountry={focusedCountry}
                    activeRegion={focusedRegion}
                  />
                )}
              </div>

              {/* Right: sticky map */}
              <div style={{
                position: 'sticky',
                top: 64,
                height: 'calc(100vh - 64px)',
              }}>
                <GlobalHeatMap
                  destinations={destinations}
                  listings={listings}
                  focusedCountry={focusedCountry}
                  focusedRegion={focusedRegion}
                  onCountryFocus={focusCountry}
                  onListingClick={goVenue}
                />
              </div>
            </div>
          )
        )}

        {loading && (
          <section style={{ padding: '80px 40px', textAlign: 'center' }}>
            <p style={{ fontFamily: NU, fontSize: 16, color: C.grey }}>Loading destinations...</p>
          </section>
        )}
      </main>

    </div>
  );
}
